import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';

import dayjs from 'dayjs';

import { BreadcrumbHeader, StatusBadge, DetailPanel, DispositionModal } from '../../components/shared';
import { rmas as rmasData } from '../../services/dataClient';
import '../../styles/design-tokens.css';

// ─── Constants ────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";
const RESOLVED = new Set(['Restocked', 'Returned to Supplier', 'Scrapped']);

// ─── Timeline step ────────────────────────────────────────────────────────────
function TimelineStep({ label, date, active, completed }) {
  const dotColor = completed ? '#009688' : active ? '#009688' : 'var(--grey-300)';
  const textColor = completed || active ? 'var(--grey-800)' : 'var(--grey-400)';

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: '2px' }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%',
          backgroundColor: dotColor,
          border: '2px solid',
          borderColor: dotColor,
          flexShrink: 0
        }} />
      </Box>
      <Box sx={{ pb: '12px' }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: textColor }}>
          {label}
        </Typography>
        {date &&
        <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '2px' }}>
            {date}
          </Typography>
        }
      </Box>
    </Box>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function RMADetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Find RMA in module-level array (includes in-session creates)
  const [rma, setRma] = useState(() => rmasData.find((r) => r.id === id) ?? null);

  const [banner, setBanner] = useState('');
  const [dispositionOpen, setDispositionOpen] = useState(false);

  const showBanner = (msg) => {
    setBanner(msg);
    setTimeout(() => setBanner(''), 4000);
  };

  // ── Not found ──────────────────────────────────────────────────────────────
  if (!rma) {
    return (
      <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <BreadcrumbHeader
          title="RMA Not Found"
          crumbs={[
          { label: 'Inventory' },
          { label: 'Reverse Logistics', path: '/inventory/reverse-logistics' },
          { label: 'Not Found' }]
          } />
        
        <ContentCard sx={{ p: 4,
          textAlign: 'center'
        }}>
          <AssignmentReturnIcon sx={{ fontSize: 48, color: 'var(--grey-300)', mb: 2 }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'var(--grey-700)', mb: 1 }}>
            RMA not found
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', mb: 3 }}>
            This RMA may have been deleted or the link is invalid.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/inventory/reverse-logistics')}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none'
            }}>
            
            Back to Reverse Logistics
          </Button>
        </ContentCard>
      </Box>);

  }

  const isResolved = RESOLVED.has(rma.status);
  const isPending = rma.status === 'Pending';
  const isReceived = rma.status === 'Received';
  const isInspecting = rma.status === 'Inspecting';

  // ── Handlers ────────────────────────────────────────────────────────────────
  const updateRma = (patch) => {
    const updated = { ...rma, ...patch };
    // Persist in module-level array
    const idx = rmasData.findIndex((r) => r.id === rma.id);
    if (idx !== -1) rmasData[idx] = updated;
    setRma(updated);
  };

  const handleMarkReceived = () => {
    updateRma({ status: 'Received', receivedDate: dayjs().format('YYYY-MM-DD') });
    showBanner(`${rma.rmaNumber} marked as Received.`);
  };

  const handleStartInspection = () => {
    updateRma({ status: 'Inspecting' });
    showBanner(`${rma.rmaNumber} moved to Inspecting.`);
  };

  const handleSaveDisposition = (result) => {
    updateRma({
      disposition: result.disposition,
      status: result.resultStatus,
      inspectionNotes: result.inspectionNotes,
      resolvedDate: RESOLVED.has(result.resultStatus) ? dayjs().format('YYYY-MM-DD') : null
    });
    showBanner(`Disposition recorded: ${result.disposition}.`);
    setDispositionOpen(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Success banner */}
      {banner &&
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px',
        backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
        borderRadius: '8px', px: 2, py: '12px'
      }}>
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {banner}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title={rma.rmaNumber}
        crumbs={[
        { label: 'Inventory' },
        { label: 'Reverse Logistics', path: '/inventory/reverse-logistics' },
        { label: rma.rmaNumber }]
        } />

      {/* ── Two-column layout ──────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

        {/* Left column — 2/3 width */}
        <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* RMA Details panel */}
          <DetailPanel
            title="RMA Details"
            fields={[
            { label: 'RMA Number', value: rma.rmaNumber },
            { label: 'Ticket #', value: rma.ticketNumber },
            { label: 'Part #', value: rma.partNumber },
            { label: 'Description', value: rma.partDescription },
            { label: 'Customer', value: rma.customerName },
            { label: 'Return Reason', value: rma.returnReason },
            { label: 'Qty Returned', value: rma.returnedQty },
            { label: 'Assigned To', value: rma.assignedTo ?? '—' }]
            } />

          {/* Inspection Notes panel */}
          <ContentCard sx={{ p: 3
          }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)', mb: 2 }}>
              Inspection Notes
            </Typography>
            {rma.inspectionNotes ?
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', lineHeight: 1.6 }}>
                {rma.inspectionNotes}
              </Typography> :

            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)', fontStyle: 'italic' }}>
                No inspection notes recorded yet.
              </Typography>
            }
          </ContentCard>

          {/* Status timeline */}
          <ContentCard sx={{ p: 3
          }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)', mb: 2 }}>
              Status Timeline
            </Typography>
            <Box sx={{ pl: 1 }}>
              <TimelineStep
                label="Created"
                date={rma.createdDate}
                completed />
              
              <TimelineStep
                label="Received"
                date={rma.receivedDate}
                completed={Boolean(rma.receivedDate)}
                active={isPending} />
              
              <TimelineStep
                label="Inspecting"
                date={isInspecting || isResolved ? rma.receivedDate ?? null : null}
                completed={isInspecting || isResolved}
                active={isReceived} />
              
              <TimelineStep
                label={rma.disposition ? `Resolved — ${rma.disposition}` : 'Resolved'}
                date={rma.resolvedDate}
                completed={isResolved}
                active={isInspecting} />
              
            </Box>
          </ContentCard>

        </Box>

        {/* Right column — 1/3 width */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Status & Disposition card */}
          <ContentCard sx={{ p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
              Status
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mb: '4px' }}>
                  Current Status
                </Typography>
                <StatusBadge status={rma.status} />
              </Box>

              {rma.disposition &&
              <Box>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mb: '4px' }}>
                    Disposition
                  </Typography>
                  <StatusBadge status={rma.disposition} />
                </Box>
              }
            </Box>

            <Divider />

            {/* Dates */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
              { label: 'Created', value: rma.createdDate },
              { label: 'Received', value: rma.receivedDate },
              { label: 'Resolved', value: rma.resolvedDate }]
              .map(({ label, value }) =>
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: value ? 'var(--grey-800)' : 'var(--grey-300)', fontWeight: value ? 500 : 400 }}>
                    {value ?? '—'}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Action buttons — status-gated */}
            {!isResolved &&
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {isPending &&
              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={handleMarkReceived}
                sx={{ backgroundColor: '#009688',
                  '&:hover': { backgroundColor: '#00796B' },
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: 'none'
                }}>
                
                    Mark as Received
                  </Button>
              }

                {isReceived &&
              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={handleStartInspection}
                sx={{ backgroundColor: '#009688',
                  '&:hover': { backgroundColor: '#00796B' },
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: 'none'
                }}>
                
                    Start Inspection
                  </Button>
              }

                {isInspecting &&
              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={() => setDispositionOpen(true)}
                sx={{ backgroundColor: '#009688',
                  '&:hover': { backgroundColor: '#00796B' },
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: 'none'
                }}>
                
                    Record Disposition
                  </Button>
              }
              </Box>
            }

            {isResolved &&
            <Box sx={{ backgroundColor: '#E8F5E9',
              border: '1px solid #A5D6A7',
              borderRadius: '6px',
              px: 2,
              py: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
                <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.8125rem', color: '#2E7D32', fontWeight: 600 }}>
                  Resolved
                </Typography>
              </Box>
            }
          </ContentCard>

          {/* Linked order card */}
          <ContentCard sx={{ p: 3
          }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)', mb: 2 }}>
              Linked Part Order
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
              { label: 'Order ID', value: rma.partOrderId, truncate: true },
              { label: 'Ticket #', value: rma.ticketNumber },
              { label: 'Part #', value: rma.partNumber }]
              .map(({ label, value, truncate }) =>
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', flexShrink: 0 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem',
                  color: 'var(--grey-800)',
                  fontWeight: 500,
                  textAlign: 'right',
                  ...(truncate && {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 120
                  })
                }}>
                    {value}
                  </Typography>
                </Box>
              )}
            </Box>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => navigate(`/orders/parts/${rma.partOrderId}`)}
              sx={{ mt: 2,
                borderColor: 'var(--grey-200)',
                color: '#009688',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                '&:hover': { borderColor: '#009688', backgroundColor: 'var(--grey-50)' }
              }}>
              
              View Part Order
            </Button>
          </ContentCard>

        </Box>
      </Box>

      {/* ── Modal ──────────────────────────────────────────────────────── */}
      <DispositionModal
        open={dispositionOpen}
        rma={rma}
        onClose={() => setDispositionOpen(false)}
        onSave={handleSaveDisposition} />

    </Box>);

}